import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Chip } from "@mui/material";

export type StatusType = "task" | "meeting" | "project" | "projectTask";

interface StatusChangeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (status: string, resolution?: string) => Promise<void>;
  currentStatus: string;
  availableStatuses: string[];
  statusType: StatusType;
  statusColors: Record<string, string>;
  isLoading?: boolean;
  currentResolution?: string;
}

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  open,
  onClose,
  onSave,
  currentStatus,
  availableStatuses,
  statusType,
  statusColors,
  isLoading = false,
  currentResolution,
}) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus);
  const [resolution, setResolution] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [localAvailableStatuses, setLocalAvailableStatuses] =
    useState<string[]>(availableStatuses);
  const [hasShownResolutionField, setHasShownResolutionField] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (availableStatuses.length === 0) {
      const defaultStatuses =
        statusType === "task" || statusType === "projectTask"
          ? ["Not Started", "Started", "Completed", "Deferred", "Сanceled"]
          : statusType === "meeting"
            ? ["Planned", "Held", "Not Held"]
            : ["Draft", "Active", "Closed", "Paused", "Canceled"];
      setLocalAvailableStatuses(defaultStatuses);
    } else {
      setLocalAvailableStatuses(availableStatuses);
    }
  }, [open, availableStatuses, statusType]);

  useEffect(() => {
    if (!open) return;

    setSelectedStatus("");
    const shouldShowResolution =
      (statusType === "task" || statusType === "projectTask") &&
      currentStatus === "Completed" &&
      !!currentResolution;
    setHasShownResolutionField(shouldShowResolution);
    if (currentStatus === "Completed" && currentResolution) {
      setResolution(currentResolution);
    } else {
      setResolution("");
    }
    setError(null);
  }, [open, statusType, currentStatus, currentResolution]);

  useEffect(() => {
    if (!open && !hasShownResolutionField && selectedStatus !== "Completed") {
      setResolution("");
    }
  }, [open, hasShownResolutionField, selectedStatus]);

  const normalizedStatus = (status: string) => {
    const normalized = status.toLowerCase().replace(/\s/g, "_");
    return normalized.replace(/сanceled/g, "canceled");
  };

  const getStatusTranslation = (status: string) => {
    const key = normalizedStatus(status);
    if (statusType === "task" || statusType === "projectTask") {
      return statusType === "projectTask"
        ? t(`statuses.projectTask.${key}`, status)
        : t(`statuses.task.${key}`, status);
    } else if (statusType === "meeting") {
      return t(`statuses.meeting.${key}`, status);
    } else {
      return t(`statuses.project.${key}`, status);
    }
  };

  const handleSave = async () => {
    setError(null);

    if (
      (statusType === "task" || statusType === "projectTask") &&
      selectedStatus === "Completed" &&
      !resolution.trim()
    ) {
      setError(
        t("tasks.detail.requiredResolution") ||
          "Результат обязателен для завершенных задач",
      );
      return;
    }

    setSaving(true);
    try {
      await onSave(selectedStatus, resolution.trim() || undefined);
      onClose();
      setTimeout(() => {
        setResolution("");
        setSelectedStatus(currentStatus);
        setHasShownResolutionField(false);
      }, 300);
    } catch (err: any) {
      setError(err.message || t("common.error") || "Произошла ошибка");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
      setTimeout(() => {
        setSelectedStatus(currentStatus);
        if (currentStatus !== "Completed" || !currentResolution) {
          setResolution("");
        }
        setHasShownResolutionField(false);
        setError(null);
      }, 300);
    }
  };

  useEffect(() => {
    if (open && (statusType === "task" || statusType === "projectTask")) {
      const shouldShow =
        selectedStatus === "Completed" ||
        (currentStatus === "Completed" && !!currentResolution);
      if (shouldShow) {
        setHasShownResolutionField(true);
      }
    }
  }, [open, selectedStatus, currentStatus, currentResolution, statusType]);

  const showResolutionField =
    (statusType === "task" || statusType === "projectTask") &&
    (open || hasShownResolutionField) &&
    (selectedStatus === "Completed" ||
      (currentStatus === "Completed" && hasShownResolutionField));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("statusChangeModal.title") || "Изменить статус"}
      </DialogTitle>
      <DialogContent>
        {localAvailableStatuses.length === 0 ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("statusChangeModal.loading") || "Загрузка статусов..."}
            </Typography>
          </Box>
        ) : (
          <List>
            {localAvailableStatuses
              .filter((status) => status !== currentStatus)
              .map((status) => {
                const statusColor = statusColors[status] || "#757575";
                const statusTranslation = getStatusTranslation(status);

                return (
                  <ListItem key={status} disablePadding>
                    <ListItemButton
                      onClick={() => setSelectedStatus(status)}
                      selected={selectedStatus === status}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        "&.Mui-selected": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selected",
                          },
                        },
                      }}
                    >
                      <Chip
                        label={statusTranslation}
                        sx={{
                          backgroundColor: statusColor,
                          color: "#fff",
                          borderRadius: "16px",
                          py: 0.5,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          height: "auto",
                          width: "100%",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>
        )}

        {showResolutionField && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label={t("tasks.detail.resolutionLabel") || "Результат"}
              fullWidth
              multiline
              minRows={3}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              required
              error={!!error && !resolution.trim()}
              helperText={error && !resolution.trim() ? error : ""}
              size="small"
            />
          </Box>
        )}

        {error && !showResolutionField && (
          <Box sx={{ mt: 2 }}>
            <ListItemText
              primary={error}
              primaryTypographyProps={{ color: "error", fontSize: "0.875rem" }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={saving || isLoading}
          sx={{ textTransform: "none" }}
        >
          {t("statusChangeModal.cancel") || "Отмена"}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saving ||
            isLoading ||
            !selectedStatus ||
            selectedStatus === currentStatus ||
            (showResolutionField && !resolution.trim())
          }
          sx={{ textTransform: "none" }}
        >
          {saving || isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("statusChangeModal.save") || "Сохранить"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusChangeModal;
